import { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import type { HebrewNatalData } from '../../utils/hebrewCalendar';

interface NatalChartResultProps {
  data: HebrewNatalData;
  userName?: string;
  interpretation: string | null;
  isLoadingInterpretation: boolean;
  onRequestInterpretation: () => void;
  onReset: () => void;
}

export default function NatalChartResult({
  data,
  userName,
  interpretation,
  isLoadingInterpretation,
  onRequestInterpretation,
  onReset,
}: NatalChartResultProps) {
  const [showTikun, setShowTikun] = useState(false);
  const { mazal } = data;
  const sefirahColor = mazal.sefirah.color;

  const cardBase = 'glass-panel rounded-2xl p-5 border border-white/5';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-2xl mx-auto space-y-4"
    >
      {/* Title */}
      <div className="text-center mb-2">
        {userName && (
          <p className="text-slate-400 text-sm mb-1">Carta natal de</p>
        )}
        <h2 className="text-2xl font-bold text-white">{userName || 'Tu carta natal hebrea'}</h2>
        <p className="text-amber-400/80 text-sm mt-1">
          {data.dayOfWeek}, {data.gregorianDate.split('-').reverse().join('/')} · {data.birthPlace}
        </p>
      </div>

      {/* Hebrew date — hero card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl p-6 text-center"
        style={{
          background: `linear-gradient(135deg, ${sefirahColor}18 0%, ${sefirahColor}08 100%)`,
          border: `1px solid ${sefirahColor}30`,
        }}
      >
        <p className="text-slate-400 text-xs uppercase tracking-widest mb-2">Fecha en el calendario hebreo</p>
        <p
          className="text-3xl sm:text-4xl font-bold mb-1 tracking-wide"
          dir="rtl"
          style={{ color: sefirahColor }}
        >
          {data.hebrewDateFormatted}
        </p>
        <p className="text-white/60 text-sm">{data.hebrewDateLatinized}</p>
        {data.isLeapYear && (
          <span className="inline-block mt-2 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-3 py-0.5">
            Año bisiesto hebreo
          </span>
        )}
        {data.afterSunsetNote && (
          <p className="text-amber-400/60 text-xs mt-2">
            ⚠ Hora post-puesta del sol — fecha ajustada al siguiente día hebreo
          </p>
        )}
      </motion.div>

      {/* Three pillars: Mazal · Tribe · Letter */}
      <div className="grid grid-cols-3 gap-3">
        {/* Mazal */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`${cardBase} text-center`}
        >
          <div className="text-3xl mb-2">{mazal.symbol}</div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Mazal</p>
          <p className="text-white font-bold text-sm">{mazal.transliteration}</p>
          <p
            className="text-lg font-bold mt-1"
            dir="rtl"
            style={{ color: sefirahColor }}
          >
            {mazal.hebrewName}
          </p>
          <p className="text-slate-400 text-xs mt-1">{mazal.spanishName}</p>
          <div className="flex items-center justify-center gap-1 mt-2">
            <span className="text-xs text-slate-600">{mazal.element}</span>
            <span className="text-slate-700">·</span>
            <span className="text-xs text-slate-600">{mazal.planet.name}</span>
          </div>
        </motion.div>

        {/* Tribe */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className={`${cardBase} text-center`}
        >
          <div className="text-3xl mb-2">⚜️</div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tribu</p>
          <p className="text-white font-bold text-sm">{mazal.tribe.name}</p>
          <p
            className="text-lg font-bold mt-1"
            dir="rtl"
            style={{ color: sefirahColor }}
          >
            {mazal.tribe.hebrew}
          </p>
          <p className="text-slate-400 text-xs mt-1">Piedra: {mazal.tribe.stone}</p>
          <div className="mt-2 flex flex-wrap justify-center gap-1">
            {mazal.tribe.qualities.slice(0, 2).map(q => (
              <span key={q} className="text-xs text-slate-600 bg-white/5 rounded px-1.5 py-0.5">{q}</span>
            ))}
          </div>
        </motion.div>

        {/* Soul Letter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`${cardBase} text-center`}
        >
          <div
            className="text-4xl font-bold mb-1 leading-none"
            dir="rtl"
            style={{ color: sefirahColor }}
          >
            {mazal.letter.letter}
          </div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Letra del alma</p>
          <p className="text-white font-bold text-sm">{mazal.letter.name}</p>
          <p className="text-slate-500 text-xs mt-1">Valor: {mazal.letter.numericalValue}</p>
          <p className="text-slate-400 text-xs mt-2 leading-snug">{mazal.letter.meaning}</p>
        </motion.div>
      </div>

      {/* Sefirah dominant */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="rounded-2xl p-5"
        style={{
          background: `linear-gradient(135deg, ${sefirahColor}15, ${sefirahColor}05)`,
          border: `1px solid ${sefirahColor}25`,
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ backgroundColor: `${sefirahColor}20`, color: sefirahColor, border: `1px solid ${sefirahColor}40` }}
          >
            ✦
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider">Sefirah dominante</p>
            <p className="text-white font-bold">
              {mazal.sefirah.name}{' '}
              <span className="font-normal text-slate-400" dir="rtl">({mazal.sefirah.hebrew})</span>
            </p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-xs text-slate-500">Vínculo divino</p>
            <p className="text-xs" style={{ color: sefirahColor }}>{mazal.sefirah.divine}</p>
          </div>
        </div>

        <p className="text-slate-300 text-sm leading-relaxed mb-3">
          <span className="font-semibold" style={{ color: sefirahColor }}>{mazal.sefirah.meaning}</span>
          {' · '}
          {mazal.sefirah.qualities.join(' · ')}
        </p>

        <div className="flex flex-wrap gap-2">
          {mazal.qualities.map(q => (
            <span
              key={q}
              className="text-xs rounded-full px-3 py-1"
              style={{ backgroundColor: `${sefirahColor}12`, color: sefirahColor, border: `1px solid ${sefirahColor}20` }}
            >
              {q}
            </span>
          ))}
        </div>

        {/* Tikun (expandable) */}
        <button
          onClick={() => setShowTikun(v => !v)}
          className="flex items-center gap-1.5 mt-4 text-xs text-slate-500 hover:text-amber-400 transition-colors"
        >
          {showTikun ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          Tu Tikún personal (misión del alma)
        </button>
        {showTikun && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-3 border-t border-white/5 pt-3"
          >
            <p className="text-slate-300 text-sm leading-relaxed italic">
              "{mazal.tikun}"
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Letter spiritual force */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className={`${cardBase}`}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl" dir="rtl" style={{ color: sefirahColor }}>
            {mazal.letter.letter}
          </span>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">
              Fuerza espiritual de tu letra — Séfer Yetzirá
            </p>
            <p className="text-slate-200 text-sm leading-relaxed">{mazal.letter.spiritualForce}</p>
          </div>
        </div>
      </motion.div>

      {/* AI Interpretation */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className={cardBase}
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <p className="text-sm font-semibold text-white">Interpretación kabbalística personalizada</p>
          <span className="text-xs text-slate-500 ml-auto">IA · Claude</span>
        </div>

        {interpretation ? (
          <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
            {interpretation}
          </div>
        ) : isLoadingInterpretation ? (
          <div className="space-y-2">
            <div className="h-3 bg-white/5 rounded animate-pulse w-full" />
            <div className="h-3 bg-white/5 rounded animate-pulse w-4/5" />
            <div className="h-3 bg-white/5 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-white/5 rounded animate-pulse w-full" />
            <div className="h-3 bg-white/5 rounded animate-pulse w-2/3" />
            <p className="text-xs text-amber-400/60 mt-3">Consultando la sabiduría kabbalística... (~30 seg)</p>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-slate-500 text-sm mb-4">
              Recibe una interpretación espiritual profunda de tu perfil kabbalístico único.
            </p>
            <button
              onClick={onRequestInterpretation}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-400 text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:from-amber-400 hover:to-yellow-300 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              Revelar interpretación AI
            </button>
          </div>
        )}
      </motion.div>

      {/* Reset */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center pt-2"
      >
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-300 text-sm transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Calcular otra carta
        </button>
      </motion.div>
    </motion.div>
  );
}
